import React, {FC, useEffect} from 'react';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import SignUpForm from './components/SignUpForm/SignUpForm';
import SignUpInterest from './components/SignUpInterest/SignUpInterest';
import FirstSignUp from './components/SignupEmailForm/SignupEmailForm';
import {useAppSelector} from '@src/Store/hooks';
import {useDispatch} from 'react-redux';
import appAuthSlice from '@src/Store/Slices/AppSlice/auth.slice';
import useEvent from 'design-system/src/Hooks/useEvent';
import useSignUpSubmit from './useSignUpSubmit';
import useSignUpForm, {
  IInterestFormPage,
} from '@src/Api/Umbraco/Hooks/useSignupForm';
import {toast} from 'react-toastify';
import useFireBaseAuthentication from '@src/Hooks/useFirebaseAuthentication';
import { AppRoutesEnum } from '@src/Constant/routes';

interface SignUpProps {
  redirect?: AppRoutesEnum | string;
}

const SignUp: FC<SignUpProps> = ({redirect}) => {
  const {t} = useTranslation('auth');
  const {submit: onSubmit} = useSignUpSubmit(redirect);
  const {
    registerStep,
    formData: initialValues,
    socialNetworkSource,
  } = useAppSelector(state => state.appAuth);
  const {
    form,
    additionnalStep: additionalSteps,
    loading: isLoading,
  } = useSignUpForm();
  const step = useAppSelector(state => state.appAuth.registerStep.page);
  const {isEmailExist} = useFireBaseAuthentication();
  const dispatch = useDispatch();
  const currentModal = useAppSelector(
    state => state.appAuth.openAuthenticationModal,
  );

  const handleSignupEmail = async (email: string) => {
    if (await isEmailExist(email)) {
      toast.error(t('Email already exist'));
      return;
    }

    dispatch(appAuthSlice.actions.setFormData({email}));
    dispatch(appAuthSlice.actions.setSocialNetworkSource('email'));
    dispatch(appAuthSlice.actions.setAuthStep({page: 'form', step: 0}));
  };

  const handleSignupForm = async (
    formData: any,
    additionalSteps: IInterestFormPage[],
  ): Promise<boolean> => {
    dispatch(appAuthSlice.actions.setFormData(formData));

    if (additionalSteps && additionalSteps.length > 0) {
      dispatch(appAuthSlice.actions.setAuthStep({page: 'interest', step: 0}));
      return true;
    }
    return await onSubmit(formData);
  };

  const handleSignUpInterest = async (
    formData: any,
    additionalSteps: IInterestFormPage[],
  ): Promise<boolean> => {
    const length = additionalSteps.length;
    dispatch(appAuthSlice.actions.setFormData(formData));

    if (registerStep.step < length - 1) {
      dispatch(
        appAuthSlice.actions.setAuthStep({
          page: 'interest',
          step: registerStep.step + 1,
        }),
      );
      return true;
    } else {
      return await onSubmit(formData);
    }
  };

  const openModal = useEvent(() => {
    if (currentModal?.open) return;
    dispatch(
      appAuthSlice.actions.openAuthenticationModal({
        open: true,
        redirect: currentModal?.redirect || '/app',
      }),
    );
  });

  useEffect(() => {
    openModal();
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: 418,
        position: 'relative',
      }}>
      {step == 'first' && (
        <FirstSignUp
          onSubmit={handleSignupEmail}
          onSignInClick={() =>
            dispatch(appAuthSlice.actions.setAuthType('login'))
          }
        />
      )}
      {step === 'form' && (
        <SignUpForm
          onSubmit={values => handleSignupForm(values, additionalSteps)}
          form={form}
          initialValues={initialValues}
          isLoading={isLoading}
          socialNetworkSource={socialNetworkSource}
        />
      )}
      {step === 'interest' && (
        <SignUpInterest
          onSubmit={values => handleSignUpInterest(values, additionalSteps)}
          additionalSteps={additionalSteps}
          initialValues={initialValues}
          registerStep={registerStep}
        />
      )}
    </div>
  );
};

export default SignUp;
